import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { computeBreakdown } from "@/lib/pricing";
import { diffDays } from "@/lib/date";
import { formatKES } from "@/lib/format";
import type { Vehicle } from "@/types";

interface BookingPanelProps {
  vehicle: Vehicle;
  start: string | null;
  end: string | null;
}

export default function BookingPanel({ vehicle, start, end }: BookingPanelProps) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState("");

  const days = start && end ? diffDays(start, end) : 0;
  const breakdown = computeBreakdown(Number(vehicle.price_per_day), days || 1);
  const hasRange = Boolean(start && end);

  async function handleReserve() {
    if (!start || !end) return;
    if (!user) {
      navigate(`/auth?redirect=/vehicle/${vehicle.id}`);
      return;
    }

    setStatus("loading");
    setError("");
    try {
      const { data, error: insertError } = await supabase
        .from("bookings")
        .insert({
          vehicle_id: vehicle.id,
          renter_id: user.id,
          renter_name: profile?.full_name || user.email || "Gari rider",
          owner_id: vehicle.owner_id,
          start_date: start,
          end_date: end,
          days: breakdown.days,
          pickup_location: vehicle.location || vehicle.city,
          daily_rate: Number(vehicle.price_per_day),
          subtotal: breakdown.subtotal,
          service_fee: breakdown.serviceFee,
          total_price: breakdown.total,
          currency: "KES",
          payment_provider: "mpesa",
          status: "pending",
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      navigate(`/checkout/${data.id}`);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "We couldn't start your booking. Please try again.");
    }
  }

  return (
    <div className="rounded-2xl surface-card p-6">
      <div className="flex items-end justify-between">
        <p>
          <span className="font-heading text-2xl font-semibold text-foreground-950">
            {formatKES(vehicle.price_per_day)}
          </span>
          <span className="text-sm text-foreground-500"> / day</span>
        </p>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-500/12 px-2.5 py-1 text-xs font-medium text-primary-400">
          <i className="ri-star-fill"></i> {Number(vehicle.rating ?? 4.8).toFixed(2)}
        </span>
      </div>

      {vehicle.instant_book && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-accent-500/12 px-3 py-2 text-xs font-medium text-accent-300">
          <i className="ri-flashlight-fill"></i> Instant book — no waiting for approval
        </p>
      )}

      <div className="mt-5 space-y-2.5 border-t border-background-200/70 pt-5">
        {hasRange ? (
          <>
            <div className="flex items-center justify-between text-sm text-foreground-700">
              <span>
                {formatKES(vehicle.price_per_day)} × {breakdown.days} {breakdown.days === 1 ? "day" : "days"}
              </span>
              <span className="text-foreground-950">{formatKES(breakdown.subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-foreground-700">
              <span className="inline-flex items-center gap-1.5">
                Gari service fee
                <i className="ri-information-line text-foreground-500" title="Covers protection, support and payments"></i>
              </span>
              <span className="text-foreground-950">{formatKES(breakdown.serviceFee)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-background-200/70 pt-3">
              <span className="font-heading text-base font-semibold text-foreground-950">Total (KES)</span>
              <span className="font-heading text-xl font-semibold text-primary-500">{formatKES(breakdown.total)}</span>
            </div>
          </>
        ) : (
          <p className="flex items-center gap-2 rounded-lg border border-dashed border-background-200 bg-background-50 px-4 py-4 text-sm text-foreground-600">
            <i className="ri-calendar-line text-primary-500"></i> Pick your dates to see the full price breakdown.
          </p>
        )}
      </div>

      {status === "error" && error && (
        <p className="mt-4 flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-3.5 py-3 text-sm text-secondary-300">
          <i className="ri-error-warning-line"></i> {error}
        </p>
      )}

      <button
        type="button"
        onClick={handleReserve}
        disabled={!hasRange || status === "loading"}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {status === "loading" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i> Starting your booking
          </>
        ) : (
          <>
            <i className="ri-secure-payment-line"></i> {user ? "Reserve & pay with M-Pesa" : "Sign in to reserve"}
          </>
        )}
      </button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
        <i className="ri-shield-check-line text-accent-400"></i> Free cancellation up to 24h before pick-up
      </p>
      {!user && (
        <p className="mt-1 text-center text-xs text-foreground-500">
          You&apos;ll be asked to sign in before confirming — your dates are kept.
        </p>
      )}
    </div>
  );
}